import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/user-avatar";
import { AvatarCropDialog } from "@/components/settings/AvatarCropDialog";
import { AvatarColorPicker } from "@/components/settings/AvatarColorPicker";
import { settingsSectionLabelClassName } from "@/components/settings/SettingsSection";
import { AVATAR_ACCEPT, useAvatarUpload } from "@/components/settings/useAvatarUpload";
import { useUserStore } from "@/hooks/useUserStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { isValidHex, normalizeHex } from "@/lib/avatarColors";
import { Camera, Loader2 } from "lucide-react";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

export interface ProfileAvatarSectionProps {
  /** The colour currently selected in the form — not yet persisted. */
  color: string;
  onColorChange: (hex: string) => void;
}

/**
 * The profile picture and the initials-avatar colour, side by side on one row.
 *
 * The picture persists on its own (upload and remove write straight to storage),
 * while the colour rides the surrounding form's submit — hence the controlled
 * `color` / `onColorChange` pair.
 */
export const ProfileAvatarSection = observer(function ProfileAvatarSection({
  color,
  onColorChange,
}: ProfileAvatarSectionProps) {
  const userStore = useUserStore();
  const { t } = useLanguage();
  const avatar = useAvatarUpload();

  const profile = userStore.userProfile;
  const selectedColor = isValidHex(color) ? normalizeHex(color) : null;

  return (
    <div className="flex flex-wrap items-center gap-6 pb-6">
      <button
        {...pageInfoTarget("settings.photo")}
        type="button"
        onClick={avatar.pickFile}
        disabled={avatar.uploading || !profile}
        className="group relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed"
        aria-label={t.settings.personal.editProfilePicture}
        title={t.settings.personal.editProfilePicture}
      >
        <UserAvatar
          user={
            profile
              ? {
                  id: profile.id,
                  first_name: profile.first_name,
                  last_name: profile.last_name,
                  email: profile.email,
                  avatar_url: profile.avatar_url,
                  color: selectedColor ?? profile.color ?? null,
                }
              : null
          }
          size={80}
          cacheBust={avatar.cacheBust}
        />
        <span className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          {avatar.uploading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <>
              <Camera className="h-5 w-5" />
              <span className="mt-1 text-xs font-medium leading-tight">
                {t.settings.personal.editProfilePicture}
              </span>
            </>
          )}
        </span>
      </button>
      <input
        ref={avatar.fileInputRef}
        type="file"
        accept={AVATAR_ACCEPT}
        className="hidden"
        onChange={avatar.fileChanged}
      />

      <div className="space-y-2">
        <div className="space-y-1">
          <h2 className={settingsSectionLabelClassName}>{t.settings.personal.profilePicture}</h2>
          <p className="text-sm text-muted-foreground">
            {t.settings.personal.avatarColorDescription}
          </p>
        </div>
        <AvatarColorPicker value={selectedColor} onChange={onColorChange} />
      </div>

      {profile?.avatar_url ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={avatar.remove}
          disabled={avatar.uploading}
        >
          {t.settings.personal.removeProfilePicture}
        </Button>
      ) : null}

      {avatar.cropSrc ? (
        <AvatarCropDialog
          open={avatar.cropOpen}
          onOpenChange={(next) => (next ? avatar.setCropOpen(true) : avatar.closeCropDialog())}
          imageSrc={avatar.cropSrc}
          mimeType={avatar.cropMime}
          onCropped={avatar.cropped}
          busy={avatar.uploading}
          labels={{
            title: t.settings.personal.cropTitle,
            description: t.settings.personal.cropDescription,
            zoom: t.settings.personal.cropZoom,
            save: t.settings.personal.cropSave,
            cancel: t.settings.personal.cropCancel,
          }}
        />
      ) : null}
    </div>
  );
});
